/* AdminMgmtPlatform — Platform Health & Operations.
 *
 * 5 tabs at /admin/platform-health:
 *   1. Feature Flags (per-tenant rollout)
 *   2. Maintenance Mode (read-only toggle)
 *   3. DB Health (collection + index inventory)
 *   4. TOS Versions (consent versioning)
 *   5. Queue Health (in-flight job queues)
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Flag, Wrench, Database, FileSignature, ListChecks, Loader2, Plus,
  PowerOff, Power, AlertOctagon, Zap, ShieldCheck,
} from "lucide-react";
import {
  MgmtKpi, MgmtSectionHeader, MgmtEmpty, ReasonField, TABS_STYLE, TabBtn,
} from "../../components/AdminMgmtCommon";
import { Switch } from "../../components/ui/switch";

const TABS = [
  { id: "flags",        label: "Feature flags",  icon: Flag },
  { id: "saas-billing", label: "SaaS billing",   icon: Zap },
  { id: "maintenance",  label: "Maintenance",    icon: Wrench },
  { id: "db",           label: "DB health",      icon: Database },
  { id: "tos",          label: "TOS versions",   icon: FileSignature },
  { id: "queues",       label: "Queue health",   icon: ListChecks },
];

export default function AdminMgmtPlatform() {
  const [tab, setTab] = useState("flags");
  return (
    <div className="p-6 space-y-5 max-w-7xl" data-testid="admin-mgmt-platform">
      <PageHeader title="Platform Health" subtitle="Feature flags · maintenance mode · DB health · TOS versions · job queues — admin oversight only."/>
      <div className={TABS_STYLE} data-testid="plat-tabs">
        {TABS.map(t => <TabBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)} icon={t.icon} label={t.label} testid={`plat-tab-${t.id}`}/>)}
      </div>
      {tab === "flags"        && <FlagsTab/>}
      {tab === "saas-billing" && <SaasBillingTab/>}
      {tab === "maintenance"  && <MaintenanceTab/>}
      {tab === "db"           && <DbHealthTab/>}
      {tab === "tos"          && <TosTab/>}
      {tab === "queues"       && <QueuesTab/>}
    </div>
  );
}

function FlagsTab() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/platform/feature-flags"); setData(r.data); }
    catch (e) { toast.error("Failed to load flags"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Feature flags" subtitle={`${data.count || 0} flags · per-tenant overrides + cohort rollout`} onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setEditing({ key: "", enabled: true, rollout_pct: 100, audience: "all" })} data-testid="flag-new-btn"><Plus className="w-3 h-3 mr-1"/>New flag</Button>}/>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="flags-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Key</th><th className="px-3 py-2">Audience</th><th className="px-3 py-2">Rollout</th><th className="px-3 py-2">Tenant overrides</th><th className="px-3 py-2">State</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(f => (
              <tr key={f.key} data-testid={`flag-row-${f.key}`}>
                <td className="px-3 py-2 mono text-[11.5px]">{f.key}</td>
                <td className="px-3 py-2"><span className="chip text-[10px] bg-foreground/5">{f.audience}</span></td>
                <td className="px-3 py-2 mono">{f.rollout_pct}%</td>
                <td className="px-3 py-2 text-foreground/55">{Object.keys(f.tenant_overrides || {}).length}</td>
                <td className="px-3 py-2"><span className={`chip text-[10px] ${f.enabled ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-foreground/5"}`}>{f.enabled ? "on" : "off"}</span></td>
                <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => setEditing(f)} data-testid={`flag-edit-${f.key}`}>Edit</Button></td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={6}><MgmtEmpty message="No flags defined yet. Create your first flag to gate features per tenant or cohort."/></td></tr>}
          </tbody>
        </table>
      </div>
      <FlagDialog editing={editing} onClose={() => setEditing(null)} onSaved={load}/>
    </div>
  );
}

function FlagDialog({ editing, onClose, onSaved }) {
  const [key, setKey] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [pct, setPct] = useState(100);
  const [audience, setAudience] = useState("all");
  const [desc, setDesc] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!editing;

  useEffect(() => {
    if (!editing) return;
    setKey(editing.key || ""); setEnabled(editing.enabled ?? true);
    setPct(editing.rollout_pct ?? 100); setAudience(editing.audience || "all");
    setDesc(editing.description || ""); setReason("");
  }, [editing]);

  const submit = async () => {
    if (key.length < 2 || reason.trim().length < 3) { toast.error("Key + reason required"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/mgmt/platform/feature-flags/${key}`, {
        key, enabled, rollout_pct: Number(pct), audience, description: desc, reason: reason.trim(),
      });
      toast.success("Flag saved"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="flag-dialog">
        <DialogHeader><DialogTitle>{editing?.key ? `Edit · ${editing.key}` : "New feature flag"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Key</Label><Input value={key} onChange={(e) => setKey(e.target.value)} disabled={!!editing?.key} placeholder="e.g. new_checkout_v2" data-testid="flag-key"/></div>
          <div className="grid grid-cols-3 gap-2">
            <label className="flex items-end gap-2 pb-1"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="flag-enabled"/><span className="text-[12.5px]">Enabled</span></label>
            <div><Label>Rollout %</Label><Input type="number" min={0} max={100} value={pct} onChange={(e) => setPct(e.target.value)} data-testid="flag-pct"/></div>
            <div>
              <Label>Audience</Label>
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="flag-audience">
                <option value="all">All</option><option value="buyer">Buyer</option><option value="seller">Seller</option><option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)}/></div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="flag-save">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MaintenanceTab() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [endAt, setEndAt] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/mgmt/platform/maintenance"); setData(r.data); setMsg(r.data.message || ""); setEndAt(r.data.scheduled_end_at || ""); }
    catch (e) { toast.error("Failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flip = async (enabled) => {
    if (reason.trim().length < 3) { toast.error("Reason ≥ 3 chars"); return; }
    setBusy(true);
    try {
      await api.put("/admin/mgmt/platform/maintenance", { enabled, message: msg, scheduled_end_at: endAt || null, reason: reason.trim() });
      toast.success(enabled ? "Maintenance ON — platform read-only" : "Maintenance OFF — platform live");
      setReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Maintenance mode" subtitle="Puts the platform into read-only mode with a banner. Safe-deploy / safe-migration toggle."/>
      <div className={`rounded-2xl border-2 p-5 ${data.enabled ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="maint-state">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {data.enabled ? <AlertOctagon className="w-6 h-6 text-rose-700"/> : <Power className="w-6 h-6 text-emerald-700"/>}
            <div>
              <div className="text-[14px] font-semibold">{data.enabled ? "Maintenance is ACTIVE" : "Platform is LIVE"}</div>
              <div className="text-[11.5px] opacity-65 mt-0.5">{data.enabled ? "All non-admin writes blocked · banner shown to all users" : "Normal operation · all writes accepted"}</div>
            </div>
          </div>
        </div>
        {data.message && <div className="mt-3 text-[12.5px] bg-background/70 rounded-md p-3">"{data.message}"</div>}
      </div>
      <div className="rounded-xl border p-4 bg-background space-y-3">
        <div><Label>Banner message (shown to all users)</Label><Textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Scheduled maintenance — read-only until 02:00 UTC" data-testid="maint-msg"/></div>
        <div><Label>Scheduled end (optional)</Label><Input type="datetime-local" value={endAt ? endAt.slice(0, 16) : ""} onChange={(e) => setEndAt(e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="maint-end"/></div>
        <ReasonField value={reason} onChange={setReason}/>
        <div className="flex gap-2 justify-end">
          {data.enabled ? (
            <Button onClick={() => flip(false)} disabled={busy || reason.trim().length < 3} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="maint-off">
              <Power className="w-3.5 h-3.5 mr-1"/>Turn OFF maintenance
            </Button>
          ) : (
            <Button onClick={() => flip(true)} disabled={busy || reason.trim().length < 3} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="maint-on">
              <PowerOff className="w-3.5 h-3.5 mr-1"/>Turn ON maintenance
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function DbHealthTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/platform/db-health"); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Mongo collections & indexes" subtitle={`${data.collections_total} collections total · top 60 shown · as of ${new Date(data.as_of).toLocaleTimeString()}`} onRefresh={load} busy={busy}/>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="db-health-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Collection</th><th className="px-3 py-2 text-right">Documents</th><th className="px-3 py-2 text-right">Indexes</th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(r => (
              <tr key={r.name} data-testid={`db-row-${r.name}`}>
                <td className="px-3 py-2 mono text-[11.5px]">{r.name}</td>
                <td className="px-3 py-2 text-right mono">{r.docs.toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-foreground/55">{r.indexes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TosTab() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/platform/tos-versions"); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="TOS / Privacy / DPA versions" subtitle={`${data.rows.length} versions published · ${data.consent_records || 0} consent records`} onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setCreateOpen(true)} data-testid="tos-new-btn"><Plus className="w-3 h-3 mr-1"/>Publish version</Button>}/>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="tos-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Kind</th><th className="px-3 py-2">Version</th><th className="px-3 py-2">Effective</th><th className="px-3 py-2">Force reconsent</th><th className="px-3 py-2">Summary</th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(r => (
              <tr key={r.id} data-testid={`tos-row-${r.id}`}>
                <td className="px-3 py-2"><span className="chip text-[10px] bg-foreground/5">{r.kind}</span></td>
                <td className="px-3 py-2 mono">{r.version}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.effective_at?.slice(0, 10)}</td>
                <td className="px-3 py-2">{r.force_reconsent ? "yes" : "no"}</td>
                <td className="px-3 py-2 max-w-md truncate text-foreground/65">{r.summary_of_changes || "—"}</td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={5}><MgmtEmpty message="No versions published yet."/></td></tr>}
          </tbody>
        </table>
      </div>
      <TosDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load}/>
    </div>
  );
}

function TosDialog({ open, onClose, onSaved }) {
  const [kind, setKind] = useState("tos");
  const [version, setVersion] = useState("");
  const [effective, setEffective] = useState("");
  const [summary, setSummary] = useState("");
  const [force, setForce] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setKind("tos"); setVersion(""); setEffective(""); setSummary(""); setForce(true); setReason(""); } }, [open]);
  const submit = async () => {
    if (version.length < 2 || !effective || reason.trim().length < 3) { toast.error("All required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/mgmt/platform/tos-versions", { version, kind, effective_at: new Date(effective).toISOString(), summary_of_changes: summary, force_reconsent: force, reason: reason.trim() });
      toast.success("Version published"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tos-dialog">
        <DialogHeader><DialogTitle>Publish new version</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Kind</Label>
              <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="tos-kind">
                <option value="tos">Terms of Service</option><option value="privacy">Privacy Policy</option><option value="dpa">DPA</option>
              </select>
            </div>
            <div><Label>Version</Label><Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. 2026.05.13" data-testid="tos-version"/></div>
          </div>
          <div><Label>Effective from</Label><Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} data-testid="tos-effective"/></div>
          <div><Label>Summary of changes</Label><Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)}/></div>
          <label className="flex items-center gap-2 text-[12.5px]"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="tos-force"/>Force re-consent on next login</label>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="tos-submit">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Publish</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QueuesTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/platform/queues"); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Job queues" subtitle={`${data.queues.length} queues monitored · as of ${new Date(data.as_of).toLocaleTimeString()}`} onRefresh={load} busy={busy}/>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="queues-grid">
        {data.queues.map(q => (
          <div key={q.name} className="rounded-xl border p-3.5 bg-background space-y-1.5" data-testid={`queue-card-${q.name}`}>
            <div className="flex items-center justify-between">
              <div className="text-[13px] font-medium">{q.label}</div>
              <span className={`chip text-[10px] ${q.failed > 0 ? "bg-rose-50 border-rose-200 text-rose-700" : q.pending > 0 ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                {q.failed > 0 ? "issues" : q.pending > 0 ? "active" : "idle"}
              </span>
            </div>
            <div className="mono text-[10.5px] text-foreground/55">{q.name}</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div><div className="text-[10px] uppercase tracking-wider text-foreground/55">Pending</div><div className="text-lg mono">{q.pending}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-foreground/55">Failed</div><div className={`text-lg mono ${q.failed > 0 ? "text-rose-700" : ""}`}>{q.failed}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-foreground/55">Done</div><div className="text-lg mono">{q.done}</div></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



/* ── SaaS Billing master kill switch ─────────────────────────────────
 * Single boolean that gates EVERY piece of the SaaS billing system
 * (cycle engine, dormant sweep, write-gate middleware, signup paywall,
 * dashboard banner, sidebar lock, invite-buyer inline counter + nudge,
 * buyer-add overage approval gate, Plan & Billing settings tab).
 *
 * OFF (default) = pre-launch — platform behaves exactly as it did before
 * SaaS billing existed. Sellers transact for free.
 *
 * ON  = LIVE — every gate becomes active. First cycle runs within the
 * hour for any subscribed tenant whose `next_billing_at` is due.
 */
function SaasBillingTab() {
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [confirming, setConfirming] = useState(null); // { to: bool }
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const r = await api.get("/admin/platform/saas-billing");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load SaaS billing status");
    } finally { setLoad(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flip = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason required (min 3 chars)");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/platform/saas-billing", {
        enforced: confirming.to,
        reason:   reason.trim(),
      });
      setData(r.data);
      toast.success(confirming.to
        ? "SaaS billing is now LIVE — cycle engine, gates, and UI are active."
        : "SaaS billing is now DORMANT — platform reverted to pre-launch behavior.");
      setConfirming(null);
      setReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-foreground/40 text-sm py-12">
        <Loader2 className="w-4 h-4 animate-spin"/> Loading…
      </div>
    );
  }

  const live = !!data?.enforced;
  const envForced = data?.env_override;   // bool | null

  return (
    <div className="space-y-5" data-testid="saas-billing-kill-switch">
      <MgmtSectionHeader
        title="SaaS billing — master kill switch"
        subtitle="ONE toggle that controls every piece of the seller SaaS billing system."
        onRefresh={load}
      />

      {/* ── Hero status card ── */}
      <div className={`rounded-2xl border p-6 flex items-start gap-4 ${live ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
        <div className={`w-12 h-12 rounded-full grid place-items-center shrink-0 ${live ? "bg-emerald-200 text-emerald-900" : "bg-amber-200 text-amber-900"}`}>
          {live ? <Power className="w-5 h-5"/> : <PowerOff className="w-5 h-5"/>}
        </div>
        <div className="flex-1">
          <div className={`text-xs uppercase tracking-widest font-medium ${live ? "text-emerald-900/70" : "text-amber-900/70"}`}>
            Current state
          </div>
          <div className={`font-medium mt-1 text-xl ${live ? "text-emerald-900" : "text-amber-900"}`}>
            {live ? "LIVE — billing is enforced" : "DORMANT — pre-launch mode"}
          </div>
          <p className={`text-[13px] mt-1 ${live ? "text-emerald-900/70" : "text-amber-900/70"} max-w-2xl`}>
            {live
              ? "Cycle engine + dormant sweep + write-gate + signup paywall + dashboard banner + sidebar lock + invite inline counter + overage approval gate are all ACTIVE. Subscribed sellers are being billed on their cycle date."
              : "Every gate is silently dormant. The platform behaves exactly as it did before SaaS billing existed — sellers transact for free, no UI surfaces are visible, no charges run."}
          </p>
        </div>
        <Switch
          checked={live}
          disabled={envForced !== null && envForced !== null}
          onCheckedChange={(next) => setConfirming({ to: next })}
          data-testid="saas-billing-master-toggle"
        />
      </div>

      {envForced !== null && envForced !== undefined && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-[12.5px] text-rose-900 flex items-start gap-2" data-testid="saas-billing-env-override">
          <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0"/>
          <div>
            <strong>Env override active:</strong> <code>SAAS_BILLING_ENFORCED={String(envForced)}</code> in the .env is locking this toggle. DB-flag is ignored until the env var is removed.
          </div>
        </div>
      )}

      {/* ── Audit / history ── */}
      <div className="rounded-2xl border p-5 bg-background">
        <div className="text-[11px] uppercase tracking-widest text-foreground/55 mb-2">Last change</div>
        {data?.last_changed_at ? (
          <div className="text-[13px] text-foreground/75 space-y-1">
            <div>When: <span className="mono text-foreground">{new Date(data.last_changed_at).toLocaleString()}</span></div>
            <div>By: <span className="mono text-foreground">{data.last_changed_by}</span></div>
            <div>Reason: <span className="italic text-foreground">{data.last_change_reason || "—"}</span></div>
          </div>
        ) : (
          <div className="text-[13px] text-foreground/55">Never toggled — current state is the system default (DORMANT).</div>
        )}
      </div>

      {/* ── What gates this controls ── */}
      <div className="rounded-2xl border p-5 bg-background">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-emerald-700"/>
          <div className="text-[13px] font-medium">Gates controlled by this switch</div>
        </div>
        <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px] text-foreground/75">
          <li>• Monthly cycle charge engine</li>
          <li>• Grace / read-only / suspended state machine</li>
          <li>• Dormant buyer auto-pause sweep</li>
          <li>• Write-gate middleware (HTTP 402)</li>
          <li>• Signup paywall (onboarding/plan redirect)</li>
          <li>• Dashboard "Choose a plan" banner</li>
          <li>• Sidebar lock when suspended</li>
          <li>• Plan &amp; Billing settings tab visibility</li>
          <li>• Invite-buyer inline cost counter</li>
          <li>• First-time overage nudge</li>
          <li>• Overage-approval confirm gate</li>
          <li>• Auto-topup from card-on-file</li>
        </ul>
      </div>

      {/* ── Confirm modal ── */}
      <Dialog open={!!confirming} onOpenChange={(v) => { if (!v) { setConfirming(null); setReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="saas-billing-confirm-modal">
          <DialogHeader>
            <DialogTitle>
              {confirming?.to ? "Activate SaaS billing?" : "Deactivate SaaS billing?"}
            </DialogTitle>
            <DialogDescription>
              {confirming?.to
                ? "All gates will become active immediately. The cycle engine will run on its next scheduled sweep (within 1 hour) and charge any subscribed seller whose next_billing_at is due."
                : "The platform will revert to pre-launch behavior. Active subscriptions stay in the DB but no charges run, no banners show, no gates fire."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-1">
            <Label className="text-[11px] uppercase tracking-widest text-foreground/55">Reason (audit-logged)</Label>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. 'Going live with paying sellers Jun 15 2026'"
              className="min-h-[80px]"
              data-testid="saas-billing-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirming(null); setReason(""); }}>Cancel</Button>
            <Button
              onClick={flip}
              disabled={saving || reason.trim().length < 3}
              className={confirming?.to ? "bg-emerald-700 hover:bg-emerald-700/90 text-white" : "bg-amber-700 hover:bg-amber-700/90 text-white"}
              data-testid="saas-billing-confirm-flip">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
              {confirming?.to ? "Activate" : "Deactivate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
